import { z } from 'zod';
import type { FacebookStatus } from '@/api/campaigns/types';
import type { TenantIntegration } from '@/api/integrations/integrationService';
import {
    computeMissingPermissions,
    FACEBOOK_REQUIRED_PERMISSIONS,
} from '../campaigns/facebookPermissions';
import { environmentOf, type CatalogField } from './catalog';

/**
 * The API masks stored secrets in responses (`••••1234`). A masked value must never be
 * sent back: it would overwrite the real secret with the mask.
 */
const MASKED_SECRET_PATTERN = /^[•*]{2,}/;

export const isMaskedSecret = (value: unknown): boolean =>
    typeof value === 'string' && MASKED_SECRET_PATTERN.test(value);

// ---------------------------------------------------------------------------
// Status derivation
// ---------------------------------------------------------------------------

export type IntegrationState = 'connected' | 'needs_attention' | 'not_set_up';

/** Why a card is in its state; drives the meta line. */
export type StatusReason =
    | 'none'
    | 'saved_not_enabled'
    | 'ok'
    | 'never_checked'
    | 'check_failed'
    | 'not_configured'
    | 'no_token'
    | 'needs_reauth'
    | 'missing_required'
    | 'missing_recommended';

export interface ChannelStatus {
    state: IntegrationState;
    reason: StatusReason;
    /** The row the card describes (the active one, else the most recently saved). */
    row?: TenantIntegration;
    /** Facebook only: the permissions behind `missing_*` reasons. */
    missing?: string[];
}

const updatedAt = (row: TenantIntegration): number => Date.parse(row.updated_at) || 0;

const newestFirst = (rows: TenantIntegration[]): TenantIntegration[] =>
    [...rows].sort((a, b) => updatedAt(b) - updatedAt(a));

/**
 * Card status for the voip / email / whatsapp / sms channels (and the generic fallback).
 *
 * - no rows                      -> not set up
 * - rows, none active            -> not set up, "saved, not enabled"
 * - active, last check failed    -> needs attention
 * - active otherwise             -> connected (checked / never checked)
 */
export function deriveChannelStatus(rows: TenantIntegration[]): ChannelStatus {
    if (rows.length === 0) {
        return { state: 'not_set_up', reason: 'none' };
    }

    const active = newestFirst(rows.filter(row => row.is_active));
    if (active.length === 0) {
        return { state: 'not_set_up', reason: 'saved_not_enabled', row: newestFirst(rows)[0] };
    }

    const failing = active.find(row => row.last_check_ok === false);
    if (failing) {
        return { state: 'needs_attention', reason: 'check_failed', row: failing };
    }

    const row = active[0];
    return {
        state: 'connected',
        reason: row.last_checked_at ? 'ok' : 'never_checked',
        row,
    };
}

export interface PaymentSummary {
    live: number;
    sandbox: number;
    /** Active providers whose last check failed. */
    failing: TenantIntegration[];
}

/** Active payment providers split by environment, plus the ones that need attention. */
export function summarizePayments(rows: TenantIntegration[]): PaymentSummary {
    const active = rows.filter(row => row.is_active);
    return {
        live: active.filter(row => environmentOf(row.configuration) === 'live').length,
        sandbox: active.filter(row => environmentOf(row.configuration) !== 'live').length,
        failing: active.filter(row => row.last_check_ok === false),
    };
}

/** Payment several may be active: needs attention as soon as one active provider fails. */
export function derivePaymentStatus(rows: TenantIntegration[]): ChannelStatus {
    if (rows.length === 0) {
        return { state: 'not_set_up', reason: 'none' };
    }

    const active = newestFirst(rows.filter(row => row.is_active));
    if (active.length === 0) {
        return { state: 'not_set_up', reason: 'saved_not_enabled', row: newestFirst(rows)[0] };
    }

    const failing = active.find(row => row.last_check_ok === false);
    if (failing) {
        return { state: 'needs_attention', reason: 'check_failed', row: failing };
    }

    const checked = active.filter(row => row.last_checked_at);
    return {
        state: 'connected',
        reason: checked.length > 0 ? 'ok' : 'never_checked',
        row: checked[0] ?? active[0],
    };
}

/**
 * Facebook card status. Comes from `GET /crm/campaigns/facebook/status` when available;
 * `integration` (the saved row) is the fallback when the user can't read campaigns.
 *
 * - no credentials / no token      -> not set up
 * - connection needs re-auth       -> needs attention
 * - required permission missing    -> needs attention
 * - recommended permission missing -> connected, with a warning meta line
 */
export function deriveFacebookStatus(
    status: FacebookStatus | undefined,
    integration?: TenantIntegration
): ChannelStatus {
    if (!status) {
        if (!integration) return { state: 'not_set_up', reason: 'none' };
        const hasToken = !!integration.configuration?.system_user_token;
        if (!hasToken) return { state: 'not_set_up', reason: 'no_token', row: integration };
        if (integration.last_check_ok === false) {
            return { state: 'needs_attention', reason: 'check_failed', row: integration };
        }
        return {
            state: integration.is_active ? 'connected' : 'not_set_up',
            reason: integration.is_active ? 'ok' : 'saved_not_enabled',
            row: integration,
        };
    }

    if (!status.configured) {
        return { state: 'not_set_up', reason: 'not_configured', row: integration };
    }
    if (!status.has_token) {
        return { state: 'not_set_up', reason: 'no_token', row: integration };
    }

    const connection = status.connection;
    if (connection?.status === 'needs_reauth') {
        return { state: 'needs_attention', reason: 'needs_reauth', row: integration };
    }

    if (connection && connection.granted_scopes) {
        const missingRequired = computeMissingPermissions(
            connection.granted_scopes,
            status.required_permissions?.length
                ? status.required_permissions
                : FACEBOOK_REQUIRED_PERMISSIONS.map(item => item.name)
        );
        if (missingRequired.length > 0) {
            return {
                state: 'needs_attention',
                reason: 'missing_required',
                row: integration,
                missing: missingRequired,
            };
        }
        const missingRecommended = connection.missing_recommended ?? [];
        if (missingRecommended.length > 0) {
            return {
                state: 'connected',
                reason: 'missing_recommended',
                row: integration,
                missing: missingRecommended,
            };
        }
    }

    if (integration?.last_check_ok === false) {
        return { state: 'needs_attention', reason: 'check_failed', row: integration };
    }

    return {
        state: 'connected',
        reason: integration?.last_checked_at ? 'ok' : 'never_checked',
        row: integration,
    };
}

export function countStates(states: IntegrationState[]): Record<IntegrationState, number> {
    return states.reduce<Record<IntegrationState, number>>(
        (acc, state) => {
            acc[state] += 1;
            return acc;
        },
        { connected: 0, needs_attention: 0, not_set_up: 0 }
    );
}

// ---------------------------------------------------------------------------
// Relative time
// ---------------------------------------------------------------------------

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 365 * 24 * 3600],
    ['month', 30 * 24 * 3600],
    ['day', 24 * 3600],
    ['hour', 3600],
    ['minute', 60],
];

/** "2 hours ago" / "منذ ساعتين"; `null` for a missing or unparsable timestamp. */
export function formatRelativeTime(
    iso: string | null | undefined,
    locale: string,
    now: number = Date.now()
): string | null {
    if (!iso) return null;
    const time = Date.parse(iso);
    if (Number.isNaN(time)) return null;

    const seconds = Math.round((time - now) / 1000);
    const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    for (const [unit, size] of UNITS) {
        if (Math.abs(seconds) >= size) {
            return formatter.format(Math.round(seconds / size), unit);
        }
    }
    return formatter.format(0, 'second');
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

export type FormValues = Record<string, string>;

export interface SchemaMessages {
    required: string;
    email: string;
    url: string;
    number: string;
    option: string;
}

/**
 * Zod schema for a list of fields (catalog provider fields or test-dialog fields).
 * Every value is a string in the form; optional fields may be empty. A masked secret
 * counts as filled, so an untouched stored secret never blocks saving.
 */
export function buildFieldsSchema(
    fields: readonly { key: string; type: string; required: boolean; options?: string[] }[],
    messages: SchemaMessages
) {
    const shape: Record<string, z.ZodTypeAny> = {};

    for (const field of fields) {
        let schema: z.ZodTypeAny = z.string().trim();

        if (field.type === 'email') {
            schema = z
                .string()
                .trim()
                .refine(value => value === '' || z.string().email().safeParse(value).success, {
                    message: messages.email,
                });
        } else if (field.type === 'url') {
            schema = z
                .string()
                .trim()
                .refine(value => value === '' || z.string().url().safeParse(value).success, {
                    message: messages.url,
                });
        } else if (field.type === 'number') {
            schema = z
                .string()
                .trim()
                .refine(
                    value =>
                        value === '' ||
                        (/^-?\d+(\.\d+)?$/.test(value) && Number.isFinite(Number(value))),
                    { message: messages.number }
                );
        } else if (field.type === 'select' && field.options?.length) {
            const options = field.options;
            schema = z
                .string()
                .trim()
                .refine(value => value === '' || options.includes(value), {
                    message: messages.option,
                });
        }

        if (field.required) {
            schema = schema.refine(value => String(value).trim() !== '', {
                message: messages.required,
            });
        }

        shape[field.key] = schema;
    }

    return z.object(shape);
}

/** Initial form values: saved configuration, else the catalog default, else empty. */
export function defaultFormValues(
    fields: readonly CatalogField[],
    configuration?: Record<string, any> | null
): FormValues {
    return Object.fromEntries(
        fields.map(field => {
            const saved = configuration?.[field.key];
            const value =
                saved !== undefined && saved !== null ? String(saved) : (field.defaultValue ?? '');
            return [field.key, value];
        })
    );
}

/**
 * The `configuration` body for POST / PUT: masked (= untouched) secrets are dropped so the
 * backend keeps the stored value; everything else the user can see is sent as typed.
 * A secret left empty on an existing integration is also dropped (clearing one is
 * "Remove", not an empty save).
 */
export function toConfiguration(
    fields: readonly CatalogField[],
    values: FormValues,
    saved?: Record<string, any> | null
): Record<string, string> {
    const configuration: Record<string, string> = {};

    for (const field of fields) {
        const value = (values[field.key] ?? '').trim();

        if (field.secret) {
            if (isMaskedSecret(value)) continue;
            if (value === '' && saved && saved[field.key] !== undefined) continue;
        }
        if (value === '' && !field.required && !saved) continue;

        configuration[field.key] = value;
    }

    return configuration;
}

export interface ServerErrors {
    /** Field key -> first message. */
    fields: Record<string, string>;
    /** Message not tied to a known field (shown above the form). */
    general?: string;
}

/**
 * Reads a 422 body `{ message, errors: { 'configuration.api_key': ['...'] } }`
 * (axios error or the body itself) into per-field messages. Keys are matched with and
 * without the `configuration.` prefix; unknown keys fall back to `general`.
 */
export function parseServerErrors(error: unknown, fieldKeys: readonly string[]): ServerErrors {
    const body =
        (error as { response?: { data?: unknown } } | null | undefined)?.response?.data ?? error;
    const record = (body && typeof body === 'object' ? body : {}) as {
        message?: unknown;
        errors?: unknown;
    };

    const fields: Record<string, string> = {};
    const leftovers: string[] = [];

    if (record.errors && typeof record.errors === 'object') {
        for (const [rawKey, value] of Object.entries(record.errors as Record<string, unknown>)) {
            const message = Array.isArray(value) ? String(value[0] ?? '') : String(value ?? '');
            if (!message) continue;
            const key = rawKey.replace(/^configuration\./, '');
            if (fieldKeys.includes(key)) {
                fields[key] ??= message;
            } else {
                leftovers.push(message);
            }
        }
    }

    const general =
        leftovers[0] ??
        (Object.keys(fields).length === 0 && typeof record.message === 'string'
            ? record.message
            : undefined);

    return { fields, general: general || undefined };
}
